import React, { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Pagination from "../../components/common/Pagination";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { BranchListQuery, fetchBranches } from "../../store/slices/branchesSlice";
import { branchOperationStatusTone, formatDate } from "../../utils/admin";

const BranchesPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { list, totalCount, page, pageSize, loadingList, listError } = useAppSelector((state) => state.branches);

  const [pageState, setPageState] = useState(1);
  const [region, setRegion] = useState("");
  const [status, setStatus] = useState<BranchListQuery["status"] | "">("");
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    dispatch(
      fetchBranches({
        region: region || undefined,
        status: status || undefined,
        q: query || undefined,
        page: pageState,
        page_size: 20,
      })
    );
  }, [dispatch, pageState, query, region, status]);

  const onSubmitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setQuery(searchInput.trim());
    setPageState(1);
  };

  return (
    <section className="admin-page">
      <h2 className="section-title">지점 관리</h2>
      <header className="list-toolbar">
        <form className="toolbar-left" onSubmit={onSubmitSearch}>
          <input
            className="filter-input"
            placeholder="지역(예: 서울)"
            value={region}
            onChange={(event) => {
              setRegion(event.target.value);
              setPageState(1);
            }}
          />
          <select
            className="chip-select"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as BranchListQuery["status"] | "");
              setPageState(1);
            }}
          >
            <option value="">전체 상태</option>
            <option value="approved">승인</option>
            <option value="pending">심사대기</option>
            <option value="rejected">반려</option>
          </select>
          <div className="search-box">
            <input
              placeholder="지점코드/지점명/주소/점장 검색"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <button className="inline-search-btn" type="submit">
              검색
            </button>
          </div>
        </form>
        <p className="summary-text">총 <strong>{totalCount.toLocaleString()}</strong>개 지점</p>
      </header>

      <div className="table-shell" style={{ overflowX: 'auto' }}>
        <table className="admin-table compact" style={{ whiteSpace: 'nowrap' }}>
          <thead>
            <tr>
              <th>지점코드</th>
              <th>지역</th>
              <th>지점명</th>
              <th>오픈일</th>
              <th>상태</th>
              <th>점장</th>
              <th>직원수</th>
              <th style={{ textAlign: 'center' }}>상세</th>
              <th style={{ textAlign: 'center' }}>직원자료</th>
              <th style={{ textAlign: 'center' }}>근무현황</th>
              <th style={{ textAlign: 'center' }}>급여대장</th>
            </tr>
          </thead>
          <tbody>
            {list.map((branch) => (
              <tr key={branch.branch_id}>
                <td>{branch.branch_code}</td>
                <td>{branch.region_label}</td>
                <td>{branch.branch_name}</td>
                <td>{formatDate(branch.opened_at)}</td>
                <td>
                  <span
                    className={`branch-status-badge branch-status-badge--${branchOperationStatusTone(branch.status)}`}
                  >
                    {branch.status}
                  </span>
                </td>
                <td>{branch.manager_name || "-"}</td>
                <td>
                  {branch.active_employee_count}/{branch.employee_count}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="detail-icon-btn"
                    type="button"
                    style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                    onClick={() => navigate(`/admin/branches/${branch.branch_id}`)}
                  >
                    상세
                  </button>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="detail-icon-btn"
                    type="button"
                    style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', backgroundColor: '#f0f5ff', color: '#5696ff', border: '1px solid #5696ff' }}
                    onClick={() => navigate(`/admin/branches/${branch.branch_id}/employees`)}
                  >
                    직원자료
                  </button>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="detail-icon-btn"
                    type="button"
                    style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', backgroundColor: '#f0f5ff', color: '#5696ff', border: '1px solid #5696ff' }}
                    onClick={() => navigate(`/admin/branches/${branch.branch_id}/work-status`)}
                  >
                    근무현황
                  </button>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="detail-icon-btn"
                    type="button"
                    style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', backgroundColor: '#f0f5ff', color: '#5696ff', border: '1px solid #5696ff' }}
                    onClick={() => navigate(`/admin/branches/${branch.branch_id}/payrolls`)}
                  >
                    급여대장
                  </button>
                </td>
              </tr>
            ))}
            {!loadingList && list.length === 0 && (
              <tr>
                <td className="empty-row" colSpan={11}>
                  조회된 지점 정보가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loadingList && <p className="loading-text">지점 목록을 불러오는 중입니다...</p>}
      {listError && <p className="error-text">{listError}</p>}
      <Pagination page={page || pageState} pageSize={pageSize || 20} totalCount={totalCount} onPageChange={setPageState} />
    </section>
  );
};

export default BranchesPage;
